/** Anonymous device identity + the offline outbox.
 *  No phone, no email, no account: sign-up friction would kill the cold start. */

const KEY_DEVICE = "fanar.device";
const KEY_OUTBOX = "fanar.outbox";

export type Device = { deviceId: string; secret: string };
export type OutboxItem = { path: string; method: "POST" | "DELETE"; body: string; queuedAt: number };

const enc = new TextEncoder();

async function hmacHex(secret: string, body: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    "raw", enc.encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", key, enc.encode(body));
  return Array.from(new Uint8Array(sig), (b) => b.toString(16).padStart(2, "0")).join("");
}

export class Session {
  private device: Device | null = null;

  constructor(private readonly apiBase: string) {
    const raw = localStorage.getItem(KEY_DEVICE);
    if (raw !== null) {
      try { this.device = JSON.parse(raw) as Device; } catch { this.device = null; }
    }
  }

  get deviceId(): string | null {
    return this.device?.deviceId ?? null;
  }

  async ensureDevice(): Promise<Device | null> {
    if (this.device !== null) return this.device;
    try {
      const res = await fetch(`${this.apiBase}/v1/devices`, { method: "POST" });
      if (!res.ok) return null;
      const dev = (await res.json()) as Device;
      this.device = dev;
      localStorage.setItem(KEY_DEVICE, JSON.stringify(dev));
      return dev;
    } catch {
      return null; // Offline at first launch: reports still queue in the outbox.
    }
  }

  private readOutbox(): OutboxItem[] {
    try { return JSON.parse(localStorage.getItem(KEY_OUTBOX) ?? "[]") as OutboxItem[]; }
    catch { return []; }
  }

  private writeOutbox(items: OutboxItem[]): void {
    localStorage.setItem(KEY_OUTBOX, JSON.stringify(items.slice(-100)));
  }

  get pendingCount(): number {
    return this.readOutbox().length;
  }

  /** Queue a write. Every item carries a clientKey, so replay is idempotent server-side. */
  enqueue(path: string, body: unknown, method: "POST" | "DELETE" = "POST"): void {
    const items = this.readOutbox();
    items.push({ path, method, body: JSON.stringify(body), queuedAt: Date.now() });
    this.writeOutbox(items);
  }

  /** Remove a queued item before it is ever sent — this is what the 6 s undo does. */
  cancelQueued(predicate: (item: OutboxItem) => boolean): boolean {
    const items = this.readOutbox();
    const kept = items.filter((i) => !predicate(i));
    if (kept.length === items.length) return false;
    this.writeOutbox(kept);
    return true;
  }

  /** One signed request, now. Returns null when offline or unregistered — the
   *  caller decides whether that means "queue it" or "tell the user". */
  async signed(
    path: string, body: unknown, method: "POST" | "DELETE" = "POST",
  ): Promise<{ status: number; json: unknown } | null> {
    const device = await this.ensureDevice();
    if (device === null) return null;
    const payload = JSON.stringify(body);
    try {
      const sig = await hmacHex(device.secret, payload);
      const res = await fetch(`${this.apiBase}${path}`, {
        method,
        headers: {
          "content-type": "application/json",
          "x-fanar-device": device.deviceId,
          "x-fanar-sig": sig,
        },
        body: payload,
      });
      let json: unknown = null;
      try { json = await res.json(); } catch { /* empty body */ }
      return { status: res.status, json };
    } catch {
      return null;
    }
  }

  async flush(): Promise<{ sent: number; failed: number }> {
    const device = await this.ensureDevice();
    if (device === null) return { sent: 0, failed: this.pendingCount };

    let sent = 0;
    const remaining: OutboxItem[] = [];
    for (const item of this.readOutbox()) {
      try {
        const sig = await hmacHex(device.secret, item.body);
        const res = await fetch(`${this.apiBase}${item.path}`, {
          method: item.method,
          headers: {
            "content-type": "application/json",
            "x-fanar-device": device.deviceId,
            "x-fanar-sig": sig,
          },
          body: item.body,
        });
        // 4xx means the server will never accept it; keeping it would retry forever.
        if (res.ok || (res.status >= 400 && res.status < 500 && res.status !== 429)) sent++;
        else remaining.push(item);
      } catch {
        remaining.push(item); // Network down: keep it for the next reconnect.
      }
    }
    this.writeOutbox(remaining);
    return { sent, failed: remaining.length };
  }
}
